import {
  BUYBOX_CONDITION_IMAGE,
  BUYBOX_SELLER_IMAGE,
  BUYBOX_SHIP_SPEED_IMAGE,
} from "../../../../formatting/buyboxInfo";
import { Box } from "@mui/material";
import React from "react";
import { GridGroupNode, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { renderDate } from "../../../../formatting/dateFormat";

// Helper function to render buy box info images based on the value
const renderBuyBoxInfoImages = (
  data: string,
  updatedAt?: string
): React.ReactNode => {
  const dataLower = data.toLowerCase();
  const titleSuffix = updatedAt ? `\n\nUpdated: ${updatedAt}` : "";

  if (!dataLower.includes("-") || dataLower.includes("ineligible")) {
    return (
      <img
        src={(BUYBOX_CONDITION_IMAGE as any)[dataLower]}
        alt={dataLower}
        title={`${dataLower}${titleSuffix}`}
      />
    );
  }

  if (dataLower.includes("amazon")) {
    let [seller, shipSpeed] = dataLower.split(" - ");

    if (seller === "temp out of stock") {
      [seller, shipSpeed] = [shipSpeed, seller];
      return (
        <div>
          <img
            src={(BUYBOX_CONDITION_IMAGE as any)[shipSpeed]}
            alt={shipSpeed}
            title={`${shipSpeed}${titleSuffix}`}
          />
          <img
            src={(BUYBOX_SELLER_IMAGE as any)[seller]}
            alt={seller}
            title={`${seller}${titleSuffix}`}
          />
        </div>
      );
    }

    if (shipSpeed)
      return (
        <div>
          <img
            src={(BUYBOX_SELLER_IMAGE as any)[seller]}
            alt={seller}
            title={`${seller}${titleSuffix}`}
          />
          <img
            src={(BUYBOX_SHIP_SPEED_IMAGE as any)[shipSpeed]}
            alt={shipSpeed}
            title={`${shipSpeed}${titleSuffix}`}
          />
        </div>
      );

    return (
      <img
        src={(BUYBOX_SELLER_IMAGE as any)[seller]}
        alt={seller}
        title={`${seller}${titleSuffix}`}
      />
    );
  }

  const [status, seller, shipSpeed] = dataLower.split(" - ");
  const statusImg = (BUYBOX_CONDITION_IMAGE as any)[status];
  const shipSpeedImg = (BUYBOX_SHIP_SPEED_IMAGE as any)[shipSpeed];

  if (seller === "not yom") {
    if (shipSpeed)
      return (
        <div>
          <img
            src={statusImg}
            alt={status}
            title={`${status} - not yom${titleSuffix}`}
          />
          <img
            src={shipSpeedImg}
            alt={shipSpeed}
            title={`${shipSpeed}${titleSuffix}`}
          />
        </div>
      );

    return (
      <img
        src={statusImg}
        alt={status}
        title={`${status} - not yom${titleSuffix}`}
      />
    );
  }

  const sellerImg = (BUYBOX_SELLER_IMAGE as any)[seller];

  if (shipSpeed)
    return (
      <div>
        <img src={statusImg} alt={status} title={`${status}${titleSuffix}`} />
        <img src={sellerImg} alt={seller} title={`${seller}${titleSuffix}`} />
        <img
          src={shipSpeedImg}
          alt={shipSpeed}
          title={`${shipSpeed}${titleSuffix}`}
        />
      </div>
    );

  return (
    <div>
      <img src={statusImg} alt={status} title={`${status}${titleSuffix}`} />
      <img src={sellerImg} alt={seller} title={`${seller}${titleSuffix}`} />
    </div>
  );
};

// Custom cell renderer for Buy Box Info that handles both group rows and leaf rows
export const BuyBoxInfoCell = (params: GridRenderCellParams) => {
  if (!params.value || typeof params.value !== "string") return null;
  const { rowNode, row } = params;

  // Check if this is a group row
  const isGroupRow = rowNode.type === "group";

  if (isGroupRow) {
    const groupNode = rowNode as GridGroupNode;

    // Handle null/empty group key
    if (row.__groupKey__ === null || row.__groupKey__ === undefined) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            ml: groupNode.depth * 2,
          }}
        >
          <Box component="span" sx={{ fontStyle: "italic", mr: 1 }}>
            (empty)
          </Box>
        </Box>
      );
    }

    // Render images for the group key value
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          ml: groupNode.depth * 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
          {renderBuyBoxInfoImages(row.__groupKey__)}
        </Box>
      </Box>
    );
  }

  // Leaf row rendering
  const data = params.value;
  if (!data || typeof data !== "string") return null;
  if (!row.amazon_product_pricing_info?.updated_at) return null;

  const updatedAt = renderDate(row.amazon_product_pricing_info.updated_at);
  return renderBuyBoxInfoImages(data, updatedAt);
};
